import React, { useId } from "react";
import { motion, useScroll, useTransform } from "motion/react";

interface GuaranteeBadgeProps extends React.SVGProps<SVGSVGElement> {
  upperText: string;
  lowerText: string;
}

export const GuaranteeBadge = React.forwardRef<
  SVGSVGElement,
  GuaranteeBadgeProps
>(({ upperText, lowerText, ...props }, ref) => {
  const id = useId();
  const upperPathId = `${id}-upper-path`;
  const lowerPathId = `${id}-lower-path`;
  const color = "#E53E51";
  const topText = upperText.toUpperCase();
  const bottomText = lowerText.toUpperCase();

  const { scrollY } = useScroll();

  const initialRotation = -45;

  const rotate = useTransform(
    scrollY,
    [0, 1000],
    [initialRotation, initialRotation + 90],
  );

  const calculateDashArray = () => {
    const idealLengths = {
      top: "10-YEAR".length,
      bottom: "GUARANTEE".length,
    };

    const lengthDifferences = {
      top: topText.length - idealLengths.top,
      bottom: bottomText.length - idealLengths.bottom,
    };

    const basePositions = {
      rightStart: 2,
      rightEnd: 3.25,
      leftStart: 8.75,
      leftEnd: 10,
    };

    const adjustedPositions = {
      rightStart: basePositions.rightStart + lengthDifferences.top * 0.25,
      rightEnd: basePositions.rightEnd - lengthDifferences.bottom * 0.25,
      leftStart: basePositions.leftStart + lengthDifferences.bottom * 0.25,
      leftEnd: basePositions.leftEnd - lengthDifferences.top * 0.25,
    };

    const circumference = 2 * Math.PI * 80;
    const hourToArc = (hour: number) => (hour / 12) * circumference;

    return [
      0,
      hourToArc(adjustedPositions.rightStart),
      hourToArc(adjustedPositions.rightEnd - adjustedPositions.rightStart),
      hourToArc(adjustedPositions.leftStart - adjustedPositions.rightEnd),
      hourToArc(adjustedPositions.leftEnd - adjustedPositions.leftStart),
      hourToArc(12 - adjustedPositions.leftEnd),
    ];
  };

  const dashArray = calculateDashArray();

  return (
    <svg
      ref={ref}
      role="img"
      aria-labelledby={`${id}-title ${id}-desc`}
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <title id={`${id}-title`}>
        {`${topText} Guarantee Badge`.toLowerCase()}
      </title>
      <desc id={`${id}-desc`}>
        A circular stamp-style badge with the text {upperText} on top and{" "}
        {lowerText} on bottom, and the BabyBjörn logo in the center.
      </desc>

      <motion.g
        style={{
          rotate,
          originX: "50%",
          originY: "50%",
        }}
        transition={{
          type: "spring",
          stiffness: 200,
          damping: 30,
        }}
      >
        {/* upper text path */}
        <path
          id={upperPathId}
          d="M20,100 A80 80 0 0,1 180,100"
          fill="none"
          stroke="none"
        />

        {/* bottom text path */}
        <path
          id={lowerPathId}
          d="M20,100 A80 80 0 0,0 180,100"
          fill="none"
          stroke="none"
        />

        {/* text along the top half */}
        <text
          fill={color}
          fontSize="28"
          fontFamily="var(--font-sans), sans-serif"
          letterSpacing="4"
          textAnchor="middle"
          dy="0.35em"
        >
          <textPath href={`#${upperPathId}`} startOffset="50%">
            {topText}
          </textPath>
        </text>

        {/* text along the bottom half */}
        <text
          fill={color}
          fontSize="28"
          fontFamily="var(--font-sans), sans-serif"
          letterSpacing="4"
          textAnchor="middle"
          dy="0.35em"
        >
          <textPath href={`#${lowerPathId}`} startOffset="50%">
            {bottomText}
          </textPath>
        </text>

        {/* circle with dash array */}
        <motion.circle
          cx="100"
          cy="100"
          r="80"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeDasharray={dashArray.join(" ")}
          transform="rotate(-90 100 100)" // Start at 12 o'clock
        />
      </motion.g>

      {/* BabyBjörn logo */}
      <g transform="translate(55, 70) scale(0.6)">
        <g clipPath="url(#clip0_3_2)">
          <path
            d="M52.1799 42.26C52.1499 45.68 49.3499 48.43 45.9299 48.4C42.5099 48.37 39.7599 45.57 39.7899 42.15C39.8199 38.73 42.6199 35.98 46.0399 36.01C49.4599 36.04 52.2099 38.84 52.1799 42.26Z"
            fill={color}
          />
          <path
            d="M114.18 42.8C114.15 46.22 111.35 48.97 107.93 48.94C104.51 48.91 101.76 46.11 101.79 42.69C101.82 39.27 104.62 36.52 108.04 36.55C111.46 36.58 114.21 39.38 114.18 42.8Z"
            fill={color}
          />
          <path
            d="M77.1099 99.28H76.8899C76.7999 99.28 76.7099 99.28 76.6199 99.28C38.2299 99.09 23.0599 76.16 21.5999 55.98C7.89995 48.95 0.0499481 38.84 -5.1934e-05 28.1C-0.0400519 20.11 3.73995 13.15 10.3599 9.01C18.9599 3.62 30.9599 3.67 42.6299 9.09C55.8399 2.47 65.4899 0 77.8699 0C91.2199 0 100.55 3.31 111.81 9.06C119.71 4.75 133 3.29 142.76 8.27C149.81 11.87 153.86 18.24 154.46 26.68C154.87 32.45 152.59 38.72 148.04 44.34C144.08 49.23 138.76 53.28 132.93 55.85C132.55 61.38 130.61 72.63 121.67 82.6C111.76 93.65 96.7799 99.26 77.1499 99.26H77.1099V99.28ZM76.66 95.03C76.76 95.03 76.8599 95.03 76.9599 95.03C90.3999 94.99 101.31 84.05 101.31 70.6C101.31 57.15 90.3499 46.17 76.8799 46.17C63.4099 46.17 52.4499 57.13 52.4499 70.6C52.4499 84.07 63.2799 94.9 76.6499 95.03H76.66ZM76.8899 41.93C92.6999 41.93 105.57 54.79 105.57 70.6C105.57 79.77 101.24 87.95 94.5199 93.21C104.53 90.9 112.57 86.41 118.52 79.78C127.59 69.66 128.66 57.76 128.77 54.35L128.81 52.96L130.1 52.44C143.46 47.09 150.83 35.29 150.24 26.98C149.74 20 146.58 14.97 140.84 12.04C132.09 7.57 119.58 9.2 113 13.27L111.98 13.91L110.91 13.36C99.76 7.58 90.7799 4.24 77.8899 4.24C65.9599 4.24 56.6799 6.71 43.6399 13.34L42.6899 13.82L41.7399 13.35C31.0999 8.13 20.2199 7.85 12.6299 12.6C7.20995 16 4.23995 21.49 4.26995 28.07C4.30995 37.42 11.7199 46.4 24.5999 52.71L25.7299 53.26L25.7899 54.52C26.0499 60.41 27.9099 71.71 37.3899 81.22C43.0099 86.86 50.2399 90.78 58.9999 92.97C52.4399 87.71 48.2299 79.63 48.2299 70.59C48.2299 54.78 61.0899 41.92 76.8999 41.92L76.8899 41.93Z"
            fill={color}
          />
          <path
            d="M89.72 77.09C87.9 77.88 85.97 78.83 84 79.31C79.89 80.32 79.37 77.99 79.37 75.55V73.97C82.21 73.14 87.66 66.68 87.66 63.6C87.66 61.69 86.68 60 85.19 59C84.92 58.77 83.09 57.44 77.27 57.44C77.27 57.44 77.27 57.44 77.26 57.44C77.25 57.44 77.26 57.44 77.25 57.44C70.89 57.44 69.29 59.04 69.29 59.04C67.83 60.05 66.87 61.72 66.87 63.61C66.87 66.69 72.33 73.16 75.16 73.98V75.56C75.16 78 74.64 80.32 70.52 79.32C68.54 78.84 66.62 77.89 64.8 77.1C63.81 76.67 62.95 77.93 63.71 78.7C67.26 82.29 72.12 84.81 77.26 84.81C82.4 84.81 87.26 82.29 90.81 78.7C91.57 77.93 90.71 76.67 89.72 77.1"
            fill={color}
          />
        </g>
        <defs>
          <clipPath>
            <rect width="154.51" height="99.28" fill="white" />
          </clipPath>
        </defs>
      </g>
    </svg>
  );
});

GuaranteeBadge.displayName = "GuaranteeBadge";
